import { useMemo, useState } from "react";
import { Link, useNavigate, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { get, api } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useFilters } from "@/hooks/useFilters";
import { todayISO, num } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Skeleton } from "@/components/ui/skeleton";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { ErrorState } from "@/components/common/States";
import { Plus, Siren, FileDown, Maximize2, Loader2, ShieldAlert } from "lucide-react";
import { toast } from "sonner";
import { cn } from "@/lib/utils";
import { Fact, OccupancyBar, AccommodationBrief, AlertList, dayLabel } from "./AccommodationBits";
import { RoomMap } from "./RoomMap";
import { RoomSheet } from "./RoomSheet";
import { StaySheet } from "./StaySheet";
import { TodayView } from "./TodayView";
import { CrewRotation, useCrewData } from "./CrewRotation";
import { RoomCalendar } from "./RoomCalendar";
import { OccupancyReport } from "./OccupancyReport";
import { MealsView } from "./MealsView";

const VIEWS = [["romkart", "Romkart"], ["kalender", "Kalender"], ["idag", "I dag"], ["crew", "Crew / rotasjon"], ["maltid", "Måltid"], ["rapport", "Rapport"], ["beredskap", "Beredskap"]];

export const useEmergencyPdf = (projectId, projectNumber) => {
  const [busy, setBusy] = useState(false);
  const download = async () => {
    setBusy(true);
    try {
      const r = await api.get(`/innkvartering/projects/${projectId}/emergency.pdf`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url; a.download = `Beredskapsliste-${projectNumber || "camp"}-${todayISO()}.pdf`; a.rel = "noopener";
      document.body.appendChild(a); a.click(); a.remove();
      setTimeout(() => URL.revokeObjectURL(url), 30_000);
      toast.success("Beredskapsliste lastet ned");
    } catch {
      toast.error("Kunne ikke lage PDF");
    } finally {
      setBusy(false);
    }
  };
  return { download, busy };
};

/**
 * Project → Innkvartering: lens header (snapshot) + Romkart · Crew/rotasjon · I dag · Beredskap.
 * All data is scoped server-side; `hasPersons` hides names on customer-operated camps.
 */
export const ProjectAccommodation = ({ projectId, projectNumber }) => {
  const { can } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const canWrite = can("innkvartering:write");
  const { filters, setFilter } = useFilters(`project-${projectId}`, { view: "romkart" });
  const view = VIEWS.some(([k]) => k === filters.view) ? filters.view : "romkart";
  const [selectedRoomId, setSelectedRoomId] = useState(null);
  const [staySheet, setStaySheet] = useState(false);
  const [stayInit, setStayInit] = useState(null); // pre-fill from the calendar (drag on an empty lane)
  // `?date=` lets the portfolio's «Neste 7 dager» deep-link straight into a given day; never persisted.
  const [date, setDate] = useState(() => (/^\d{4}-\d{2}-\d{2}$/.test(searchParams.get("date") || "") ? searchParams.get("date") : todayISO()));
  const [crewSel, setCrewSel] = useState(null);

  const snap = useQuery({ queryKey: ["ink", "snapshot", projectId], queryFn: () => get(`/innkvartering/projects/${projectId}/snapshot`), refetchInterval: 120_000 });
  const rooms = useQuery({ queryKey: ["ink", "rooms", projectId], queryFn: () => get(`/innkvartering/projects/${projectId}/rooms`), enabled: view === "romkart" || Boolean(selectedRoomId) });
  const today = useQuery({ queryKey: ["ink", "today", projectId, date], queryFn: () => get(`/innkvartering/projects/${projectId}/today`, { date }), enabled: view === "idag" });
  const crews = useCrewData(projectId, view === "crew");
  const persons = useQuery({ queryKey: ["ink", "persons", projectId], queryFn: () => get(`/innkvartering/projects/${projectId}/persons`), enabled: canWrite && Boolean(snap.data?.has_person_data) });
  const emergency = useQuery({ queryKey: ["ink", "emergency", projectId], queryFn: () => get(`/innkvartering/projects/${projectId}/emergency`), enabled: view === "beredskap" });
  const pdf = useEmergencyPdf(projectId, projectNumber);

  const s = snap.data;
  const k = s?.kpis;
  const hasPersons = Boolean(s?.has_person_data);
  const selectedRoom = useMemo(() => rooms.data?.rooms?.find((r) => r.room_id === selectedRoomId) || null, [rooms.data, selectedRoomId]);
  const crewList = useMemo(() => (crews.data?.crews || s?.crews || []).map((c) => ({ id: c.id, name: c.name, employer_name: c.employer_name })), [crews.data, s]);

  if (snap.isError) return <ErrorState error={snap.error} onRetry={() => snap.refetch()} className="py-12" />;
  if (!s) return <div className="grid gap-4 sm:grid-cols-3 lg:grid-cols-6" data-testid="project-accommodation-loading">{[...Array(6)].map((_, i) => <Skeleton key={i} className="h-16" />)}</div>;
  if (!k?.beds_total) {
    return (
      <div className="rounded-lg border border-dashed px-6 py-12 text-center" data-testid="project-accommodation-empty">
        <p className="text-[14px] font-medium">Ingen rom er plassert på prosjektet</p>
        <p className="mt-1 text-[12.5px] text-muted-foreground">Romkartet bygges automatisk fra boligmodulene som allokeres til prosjektet.</p>
      </div>
    );
  }
  const occTone = k.occupancy_pct >= 95 ? "danger" : k.occupancy_pct >= 85 ? "warning" : undefined;

  return (
    <div className="space-y-6" data-testid="project-accommodation">
      {/* lens header: facts full width, then brief + alerts */}
      <div className="space-y-5">
        <div>
          <div className="grid grid-cols-3 gap-x-5 gap-y-4 sm:grid-cols-6" data-testid="accommodation-lens-facts">
            <Fact label="Senger" value={num(k.beds_total)} sub={k.beds_out ? `${k.beds_available} tilgjengelig` : `${k.rooms_total} rom`} testId="accommodation-fact-beds" />
            <Fact label="Innsjekket" value={hasPersons ? num(k.on_site) : "–"} sub={hasPersons ? `${k.occupancy_pct} % belegg` : "kunde drifter"} tone={occTone} testId="accommodation-fact-on-site" />
            <Fact label="Ledig & klar" value={hasPersons ? num(k.free_ready_beds) : "–"} sub="senger" tone={hasPersons && k.free_ready_beds === 0 ? "warning" : undefined} testId="accommodation-fact-free" />
            <Fact label="Må rengjøres" value={num(k.rooms_to_clean)} sub="rom" tone={k.rooms_to_clean ? "warning" : undefined} testId="accommodation-fact-clean" onClick={() => setFilter("view", "idag")} />
            <Fact label="Ute av drift" value={num(k.rooms_out_of_service)} sub="rom" tone={k.rooms_out_of_service ? "danger" : undefined} testId="accommodation-fact-out" onClick={() => setFilter("view", "idag")} />
            <Fact label="Neste bytte" value={s.next_handover ? dayLabel(s.next_handover.date) : "–"} mono={false} sub={s.next_handover ? `${s.next_handover.net >= 0 ? "+" : ""}${s.next_handover.net} personer` : "ingen planlagt"} testId="accommodation-fact-handover" onClick={() => setFilter("view", "crew")} />
          </div>
          {hasPersons && <OccupancyBar pct={k.occupancy_pct} tone={occTone} className="mt-4" testId="accommodation-occupancy-bar" />}
        </div>
        <div className="grid gap-5 lg:grid-cols-12">
          <AccommodationBrief sentences={s.brief} compact testId="accommodation-brief" className="lg:col-span-8" />
          {s.alerts?.length > 0 && <AlertList alerts={s.alerts} className="lg:col-span-4 lg:border-l lg:border-border/70 lg:pl-5" />}
        </div>
      </div>

      {/* views */}
      <Tabs value={view} onValueChange={(v) => setFilter("view", v)}>
        <div className="flex flex-wrap items-center gap-3">
          <TabsList className="h-auto flex-wrap" data-testid="accommodation-view-tabs">
            {VIEWS.map(([k2, l]) => <TabsTrigger key={k2} value={k2} data-testid={`accommodation-view-${k2}`}>{l}</TabsTrigger>)}
          </TabsList>
          <div className="ml-auto flex items-center gap-2">
            {canWrite && hasPersons && view !== "beredskap" && <Button size="sm" className="h-8" onClick={() => { setStayInit(null); setStaySheet(true); }} data-testid="accommodation-add-stay-button"><Plus /> Legg inn opphold</Button>}
            <Button size="sm" variant="outline" className="h-8" onClick={() => navigate(`/prosjekter/${projectId}/beredskap`)} data-testid="accommodation-open-emergency-button"><Siren className="text-rose-600" /> Beredskap</Button>
          </div>
        </div>
      </Tabs>

      {view === "romkart" && (rooms.isLoading ? <Skeleton className="h-72" data-testid="room-map-loading" /> : rooms.isError ? <ErrorState error={rooms.error} onRetry={() => rooms.refetch()} /> : (
        <RoomMap data={rooms.data} selectedId={selectedRoomId} onSelect={(r) => setSelectedRoomId(r?.room_id || null)} canWrite={canWrite} />
      ))}
      {view === "kalender" && (
        <RoomCalendar projectId={projectId} canWrite={canWrite} hasPersons={hasPersons} onOpenRoom={(id) => setSelectedRoomId(id)} onCreateStay={(init) => { setStayInit(init); setStaySheet(true); }} />
      )}
      {view === "crew" && <CrewRotation projectId={projectId} data={crews.data} isLoading={crews.isLoading} canWrite={canWrite} hasPersons={hasPersons} selectedCrewId={crewSel} onSelectCrew={setCrewSel} />}
      {view === "idag" && <TodayView data={today.data} isLoading={today.isLoading} date={date} onDate={setDate} canWrite={canWrite} hasPersons={hasPersons} />}
      {view === "maltid" && <MealsView projectId={projectId} projectNumber={projectNumber} hasPersons={hasPersons} />}
      {view === "rapport" && <OccupancyReport projectId={projectId} projectNumber={projectNumber} />}
      {view === "beredskap" && (
        <div className="grid gap-6 lg:grid-cols-12" data-testid="accommodation-emergency-summary">
          <div className="lg:col-span-7">
            {emergency.isLoading || !emergency.data ? <Skeleton className="h-40" /> : (
              <div className="space-y-4">
                <div className="grid grid-cols-3 gap-4">
                  <Fact label="På camp nå" value={num(emergency.data.on_site)} sub="innsjekket" size="lg" testId="emergency-summary-on-site" />
                  <Fact label="Forventet" value={num(emergency.data.expected_not_checked_in)} sub="ikke innsjekket" tone={emergency.data.expected_not_checked_in ? "warning" : undefined} size="lg" testId="emergency-summary-expected" />
                  <Fact label="Rom i bruk" value={num(emergency.data.rooms_occupied)} sub={`av ${k.rooms_total}`} size="lg" testId="emergency-summary-rooms" />
                </div>
                <ul className="divide-y rounded-lg border text-[13px]" data-testid="emergency-summary-buildings">
                  {emergency.data.buildings.map((b) => (
                    <li key={b.building} className="flex items-center justify-between px-3 py-2"><span>Bygg {b.building} <span className="text-muted-foreground">· {b.floors.map((f) => `etasje ${f.floor}: ${f.count}`).join(" · ")}</span></span><span className="font-mono font-semibold tabular">{b.count}</span></li>
                  ))}
                  {!emergency.data.buildings.length && <li className="px-3 py-3 text-muted-foreground">Ingen innsjekket akkurat nå.</li>}
                </ul>
                <p className="text-[12px] text-muted-foreground">Øyeblikksbilde {emergency.data.generated_label}. Åpningen logges i audit.</p>
              </div>
            )}
          </div>
          <div className="space-y-3 lg:col-span-5 lg:border-l lg:border-border/70 lg:pl-6">
            <div className="flex items-start gap-3 rounded-lg border border-rose-500/30 bg-rose-500/[0.05] p-4">
              <ShieldAlert className="mt-0.5 h-5 w-5 shrink-0 text-rose-600" />
              <div className="text-[13px] leading-5">
                <div className="font-semibold">Beredskapsoversikt</div>
                <p className="mt-0.5 text-foreground/80">Hvem er på campen akkurat nå – gruppert per bygg, etasje og rom, med telefon og arbeidsgiver. Til mønstring, brann og evakuering.</p>
              </div>
            </div>
            <div className="grid gap-2 sm:grid-cols-2">
              <Button onClick={() => navigate(`/prosjekter/${projectId}/beredskap`)} data-testid="accommodation-emergency-fullscreen-button"><Maximize2 /> Åpne fullskjerm</Button>
              <Button variant="outline" onClick={pdf.download} disabled={pdf.busy} data-testid="accommodation-emergency-pdf-button">{pdf.busy ? <Loader2 className="animate-spin" /> : <FileDown />} Last ned PDF</Button>
            </div>
            <p className="text-[11.5px] leading-4 text-muted-foreground">PDF-en er monokrom og laget for utskrift ved mønstringspunktet. Persondata makuleres etter bruk.</p>
            <Link to={`/prosjekter/${projectId}?tab=aktivitet`} className={cn("text-[12px] text-muted-foreground hover:text-foreground")}>Se logg over inn-/utsjekk under Aktivitet</Link>
          </div>
        </div>
      )}

      <RoomSheet room={selectedRoom} open={Boolean(selectedRoom)} onOpenChange={(o) => !o && setSelectedRoomId(null)} projectId={projectId} canWrite={canWrite} hasPersons={hasPersons} persons={persons.data || []} crews={crewList} />
      <StaySheet open={staySheet} onOpenChange={(o) => { setStaySheet(o); if (!o) setStayInit(null); }} projectId={projectId} mode="create" initial={stayInit} persons={persons.data || []} crews={crewList} />
    </div>
  );
};
